(() => {
  'use strict';

  const DESKTOP_MEDIA_QUERY = window.matchMedia(
    'screen and (min-width: 1008px)'
  );

  const GALLERY_SELECTOR =
    '.Product__Gallery--stack.Product__Gallery--withThumbnails';

  const THUMBNAIL_SELECTOR =
    '.Product__SlideshowNavImage[data-media-id]';

  const SLIDE_SELECTOR =
    '.Product__SlideItem[data-media-id]';

  const IMAGE_SLIDE_SELECTOR =
    '.Product__SlideItem--image[data-media-id]';

  const VIEWER_CLASS = 'MooaImageViewer';

  const MAX_SCALE = 3;
  const SCALE_STEP = 0.25;

  let activeViewer = null;


  /* =========================================================
     UTILIDADES
     ========================================================= */

  function pauseMedia(slide) {
    if (!(slide instanceof HTMLElement)) {
      return;
    }

    slide
      .querySelectorAll('video')
      .forEach((video) => {
        if (!video.paused) {
          video.pause();
        }
      });
  }


  function getSlides(gallery) {
    return Array.from(
      gallery.querySelectorAll(
        SLIDE_SELECTOR
      )
    );
  }


  function getImageSlides(gallery) {
    return Array.from(
      gallery.querySelectorAll(
        IMAGE_SLIDE_SELECTOR
      )
    );
  }


  function getThumbnails(gallery) {
    return Array.from(
      gallery.querySelectorAll(
        THUMBNAIL_SELECTOR
      )
    );
  }


  /* =========================================================
     PRECARGA DE IMÁGENES — MÓVIL / TABLET
     ========================================================= */

  function getBestImageSource(image) {
    if (!(image instanceof HTMLImageElement)) {
      return '';
    }

    /*
     * Shopify / lazysizes puede guardar
     * la imagen real en distintos atributos.
     */

    const original =
      image.getAttribute('data-original-src');

    if (original) {
      return original;
    }


    const srcset =
      image.getAttribute('data-srcset') ||
      image.getAttribute('srcset');


    if (srcset) {
      const candidates =
        srcset
          .split(',')
          .map((item) =>
            item.trim()
          )
          .filter(Boolean);


      const lastCandidate =
        candidates[
          candidates.length - 1
        ];


      if (lastCandidate) {
        return (
          lastCandidate
            .split(/\s+/)[0] ||
          ''
        );
      }
    }


    return (
      image.getAttribute('data-src') ||
      image.currentSrc ||
      image.src ||
      ''
    );
  }


  function preloadImage(image) {
    if (
      !(image instanceof HTMLImageElement)
    ) {
      return;
    }


    /*
     * Pedimos a lazysizes que cargue
     * la imagen inmediatamente si todavía
     * está marcada como lazy.
     */

    if (
      window.lazySizes &&
      typeof window.lazySizes.loader
        ?.unveil === 'function'
    ) {
      try {
        window.lazySizes.loader.unveil(
          image
        );
      } catch (error) {
        /*
         * Si lazysizes no puede hacerlo,
         * continuamos con la precarga manual.
         */
      }
    }


    const source =
      getBestImageSource(image);


    if (!source) {
      return;
    }


    /*
     * El navegador reutiliza esta descarga
     * cuando posteriormente necesita mostrarla.
     */

    const preload =
      new Image();


    preload.decoding = 'async';
    preload.src = source;
  }


  function preloadMobileGallery(
    gallery
  ) {
    if (
      !(gallery instanceof HTMLElement) ||
      DESKTOP_MEDIA_QUERY.matches
    ) {
      return;
    }


    if (
      gallery.dataset
        .mooaMobilePreloaded ===
      'true'
    ) {
      return;
    }


    gallery.dataset
      .mooaMobilePreloaded =
      'true';


    const slides =
      getImageSlides(gallery);


    if (slides.length === 0) {
      return;
    }


    /*
     * Cargamos primero las dos imágenes
     * siguientes, que son las más probables
     * al hacer swipe.
     */

    slides
      .slice(0, 3)
      .forEach((slide) => {

        const image =
          slide.querySelector('img');

        preloadImage(image);

      });


    /*
     * El resto se precarga ligeramente
     * después para no bloquear la carga
     * inicial de la ficha.
     */

    const preloadRest = () => {

      slides
        .slice(3)
        .forEach((slide) => {

          const image =
            slide.querySelector('img');

          preloadImage(image);

        });

    };


    if (
      'requestIdleCallback'
      in window
    ) {

      window.requestIdleCallback(
        preloadRest,
        {
          timeout: 1200
        }
      );

    } else {

      window.setTimeout(
        preloadRest,
        500
      );

    }

  }


  function preloadAllMobileGalleries(
    root = document
  ) {

    if (
      DESKTOP_MEDIA_QUERY.matches
    ) {
      return;
    }


    if (
      root instanceof HTMLElement &&
      root.matches(
        GALLERY_SELECTOR
      )
    ) {
      preloadMobileGallery(root);
    }


    root
      .querySelectorAll?.(
        GALLERY_SELECTOR
      )
      .forEach(
        preloadMobileGallery
      );

  }


  /* =========================================================
     SELECCIÓN DE MEDIA — DESKTOP
     ========================================================= */

  function selectMedia(
    gallery,
    mediaId,
    options = {}
  ) {

    const {
      focusSlide = false,
      scrollThumbnail = true
    } = options;


    if (
      !(gallery instanceof HTMLElement) ||
      !mediaId
    ) {
      return;
    }


    const slides =
      getSlides(gallery);

    const thumbnails =
      getThumbnails(gallery);


    const selectedSlide =
      slides.find(
        (slide) =>
          String(
            slide.dataset.mediaId
          ) ===
          String(mediaId)
      );


    const selectedThumbnail =
      thumbnails.find(
        (thumbnail) =>
          String(
            thumbnail.dataset.mediaId
          ) ===
          String(mediaId)
      );


    if (!selectedSlide) {
      return;
    }


    slides.forEach((slide) => {

      const isSelected =
        slide === selectedSlide;


      if (!isSelected) {
        pauseMedia(slide);
      }


      slide.classList.toggle(
        'is-selected',
        isSelected
      );


      slide.setAttribute(
        'aria-hidden',
        isSelected
          ? 'false'
          : 'true'
      );


      slide.tabIndex =
        isSelected
          ? 0
          : -1;

    });


    thumbnails.forEach(
      (thumbnail) => {

        const isSelected =
          thumbnail ===
          selectedThumbnail;


        thumbnail.classList.toggle(
          'is-selected',
          isSelected
        );


        thumbnail.setAttribute(
          'aria-current',
          isSelected
            ? 'true'
            : 'false'
        );

      }
    );


    if (
      scrollThumbnail &&
      selectedThumbnail
        instanceof HTMLElement
    ) {

      selectedThumbnail
        .scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest'
        });

    }


    if (focusSlide) {

      selectedSlide.focus({
        preventScroll: true
      });

    }

  }


  function getInitialMediaId(
    gallery
  ) {

    const selectedSlide =
      gallery.querySelector(
        `${SLIDE_SELECTOR}.is-selected`
      );


    if (
      selectedSlide?.dataset.mediaId
    ) {
      return selectedSlide
        .dataset.mediaId;
    }


    const selectedThumbnail =
      gallery.querySelector(
        `${THUMBNAIL_SELECTOR}.is-selected`
      );


    if (
      selectedThumbnail?.dataset
        .mediaId
    ) {
      return selectedThumbnail
        .dataset.mediaId;
    }


    return (
      gallery.querySelector(
        SLIDE_SELECTOR
      )?.dataset.mediaId ||
      null
    );

  }


  /* =========================================================
     INICIALIZACIÓN
     ========================================================= */

  function initialiseGallery(
    gallery
  ) {

    if (
      !(gallery instanceof HTMLElement) ||
      gallery.dataset
        .mooaGalleryReady ===
        'true'
    ) {
      return;
    }


    gallery.dataset
      .mooaGalleryReady =
      'true';


    gallery.classList.add(
      'MooaProductGallery'
    );


    const initialMediaId =
      getInitialMediaId(
        gallery
      );


    if (
      initialMediaId &&
      DESKTOP_MEDIA_QUERY.matches
    ) {

      selectMedia(
        gallery,
        initialMediaId,
        {
          scrollThumbnail: false
        }
      );

    }


    /*
     * En móvil no sustituimos el carrusel
     * nativo de Prestige.
     *
     * Solo precargamos sus imágenes.
     */

    if (
      !DESKTOP_MEDIA_QUERY.matches
    ) {
      preloadMobileGallery(
        gallery
      );
    }

  }


  function initialiseAllGalleries(
    root = document
  ) {

    if (
      root instanceof HTMLElement &&
      root.matches(
        GALLERY_SELECTOR
      )
    ) {
      initialiseGallery(root);
    }


    root
      .querySelectorAll?.(
        GALLERY_SELECTOR
      )
      .forEach(
        initialiseGallery
      );

  }


  /* =========================================================
     VISOR DESKTOP
     ========================================================= */

  function getFullImageSource(
    image
  ) {

    if (
      !(image instanceof HTMLImageElement)
    ) {
      return '';
    }


    const original =
      image.getAttribute(
        'data-original-src'
      );


    if (original) {
      return original;
    }


    return (
      image.currentSrc ||
      image.src ||
      ''
    );

  }


  function getViewerItems(
    gallery
  ) {

    return getImageSlides(
      gallery
    )
      .map((slide) => {

        const image =
          slide.querySelector(
            'img'
          );


        const src =
          getFullImageSource(
            image
          );


        if (!src) {
          return null;
        }


        return {
          mediaId:
            slide.dataset.mediaId,

          src,

          alt:
            image?.alt ||
            ''
        };

      })
      .filter(Boolean);

  }


  function createViewerMarkup() {

    const viewer =
      document.createElement(
        'div'
      );


    viewer.className =
      VIEWER_CLASS;


    viewer.setAttribute(
      'role',
      'dialog'
    );


    viewer.setAttribute(
      'aria-modal',
      'true'
    );


    viewer.setAttribute(
      'aria-label',
      'Galería ampliada del producto'
    );


    viewer.setAttribute(
      'aria-hidden',
      'true'
    );


    viewer.innerHTML = `
      <div
        class="MooaImageViewer__Backdrop"
        data-mooa-viewer-close
      ></div>

      <div class="MooaImageViewer__Stage">
        <img
          class="MooaImageViewer__Image"
          alt=""
        >
      </div>

      <button
        type="button"
        class="MooaImageViewer__Button MooaImageViewer__Close"
        aria-label="Cerrar imagen ampliada"
        data-mooa-viewer-close
      >
        <span aria-hidden="true">×</span>
      </button>

      <button
        type="button"
        class="MooaImageViewer__Button MooaImageViewer__Previous"
        aria-label="Imagen anterior"
        data-mooa-viewer-previous
      >
        <span aria-hidden="true">‹</span>
      </button>

      <button
        type="button"
        class="MooaImageViewer__Button MooaImageViewer__Next"
        aria-label="Imagen siguiente"
        data-mooa-viewer-next
      >
        <span aria-hidden="true">›</span>
      </button>

      <div
        class="MooaImageViewer__Counter"
        aria-live="polite"
      ></div>
    `;


    document.body.appendChild(
      viewer
    );


    return viewer;

  }


  function closeViewer() {

    if (!activeViewer) {
      return;
    }


    const {
      element,
      opener
    } = activeViewer;


    element.classList.remove(
      'is-open'
    );


    element.setAttribute(
      'aria-hidden',
      'true'
    );


    document.documentElement
      .classList.remove(
        'MooaImageViewerOpen'
      );


    window.setTimeout(() => {

      element.remove();

    }, 220);


    opener?.focus?.({
      preventScroll: true
    });


    activeViewer = null;

  }


  function renderViewerItem() {

    if (!activeViewer) {
      return;
    }


    const {
      element,
      items,
      index
    } = activeViewer;


    const item =
      items[index];


    const image =
      element.querySelector(
        '.MooaImageViewer__Image'
      );


    const counter =
      element.querySelector(
        '.MooaImageViewer__Counter'
      );


    const previous =
      element.querySelector(
        '[data-mooa-viewer-previous]'
      );


    const next =
      element.querySelector(
        '[data-mooa-viewer-next]'
      );


    activeViewer.scale = 1;
    activeViewer.translateX = 0;
    activeViewer.translateY = 0;


    image.classList.add(
      'is-loading'
    );


    image.style.transform =
      'translate3d(0, 0, 0) scale(1)';


    image.alt =
      item.alt;


    image.onload = () => {

      image.classList.remove(
        'is-loading'
      );

    };


    image.src =
      item.src;


    counter.textContent =
      `${index + 1} / ${items.length}`;


    previous.disabled =
      index === 0;


    next.disabled =
      index ===
      items.length - 1;

  }


  function changeViewerItem(
    direction
  ) {

    if (!activeViewer) {
      return;
    }


    const nextIndex =
      activeViewer.index +
      direction;


    if (
      nextIndex < 0 ||
      nextIndex >=
        activeViewer.items.length
    ) {
      return;
    }


    activeViewer.index =
      nextIndex;


    renderViewerItem();

  }


  function applyViewerTransform() {

    if (!activeViewer) {
      return;
    }


    const image =
      activeViewer.element
        .querySelector(
          '.MooaImageViewer__Image'
        );


    image.style.transform =
      `translate3d(
        ${activeViewer.translateX}px,
        ${activeViewer.translateY}px,
        0
      ) scale(${activeViewer.scale})`;


    image.classList.toggle(
      'is-zoomed',
      activeViewer.scale > 1
    );

  }


  function setViewerScale(
    nextScale,
    origin = null
  ) {

    if (!activeViewer) {
      return;
    }


    const previousScale =
      activeViewer.scale;


    const scale =
      Math.min(
        MAX_SCALE,
        Math.max(
          1,
          nextScale
        )
      );


    if (
      origin &&
      scale !==
        previousScale
    ) {

      const stage =
        activeViewer.element
          .querySelector(
            '.MooaImageViewer__Stage'
          );


      const rect =
        stage
          .getBoundingClientRect();


      const x =
        origin.clientX -
        (
          rect.left +
          rect.width / 2
        );


      const y =
        origin.clientY -
        (
          rect.top +
          rect.height / 2
        );


      const ratio =
        scale /
        previousScale;


      activeViewer.translateX =
        x -
        (
          x -
          activeViewer.translateX
        ) *
        ratio;


      activeViewer.translateY =
        y -
        (
          y -
          activeViewer.translateY
        ) *
        ratio;

    }


    activeViewer.scale =
      scale;


    if (scale === 1) {

      activeViewer.translateX =
        0;

      activeViewer.translateY =
        0;

    }


    applyViewerTransform();

  }


  function openViewer(
    gallery,
    mediaId,
    opener
  ) {

    const items =
      getViewerItems(
        gallery
      );


    if (
      items.length === 0
    ) {
      return;
    }


    const selectedIndex =
      Math.max(
        0,
        items.findIndex(
          (item) =>
            String(
              item.mediaId
            ) ===
            String(
              mediaId
            )
        )
      );


    closeViewer();


    const element =
      createViewerMarkup();


    activeViewer = {
      element,
      items,
      index:
        selectedIndex,
      opener,

      scale: 1,

      translateX: 0,
      translateY: 0,

      dragging: false,

      dragStartX: 0,
      dragStartY: 0,

      startTranslateX: 0,
      startTranslateY: 0
    };


    element.addEventListener(
      'click',
      (event) => {

        if (
          event.target.closest(
            '[data-mooa-viewer-close]'
          )
        ) {

          closeViewer();

          return;

        }


        if (
          event.target.closest(
            '[data-mooa-viewer-previous]'
          )
        ) {

          changeViewerItem(
            -1
          );

          return;

        }


        if (
          event.target.closest(
            '[data-mooa-viewer-next]'
          )
        ) {

          changeViewerItem(
            1
          );

        }

      }
    );


    const stage =
      element.querySelector(
        '.MooaImageViewer__Stage'
      );


    const image =
      element.querySelector(
        '.MooaImageViewer__Image'
      );


    stage.addEventListener(
      'wheel',
      (event) => {

        event.preventDefault();


        const direction =
          event.deltaY < 0
            ? SCALE_STEP
            : -SCALE_STEP;


        setViewerScale(
          activeViewer.scale +
          direction,
          event
        );

      },
      {
        passive: false
      }
    );


    image.addEventListener(
      'dblclick',
      (event) => {

        event.preventDefault();


        setViewerScale(
          activeViewer.scale > 1
            ? 1
            : 2,
          event
        );

      }
    );


    image.addEventListener(
      'pointerdown',
      (event) => {

        if (
          !activeViewer ||
          activeViewer.scale <= 1
        ) {
          return;
        }


        activeViewer.dragging =
          true;


        activeViewer.dragStartX =
          event.clientX;


        activeViewer.dragStartY =
          event.clientY;


        activeViewer.startTranslateX =
          activeViewer.translateX;


        activeViewer.startTranslateY =
          activeViewer.translateY;


        image.setPointerCapture?.(
          event.pointerId
        );


        image.classList.add(
          'is-dragging'
        );

      }
    );


    image.addEventListener(
      'pointermove',
      (event) => {

        if (
          !activeViewer?.dragging
        ) {
          return;
        }


        activeViewer.translateX =
          activeViewer.startTranslateX +
          event.clientX -
          activeViewer.dragStartX;


        activeViewer.translateY =
          activeViewer.startTranslateY +
          event.clientY -
          activeViewer.dragStartY;


        applyViewerTransform();

      }
    );


    const stopDragging = () => {

      if (!activeViewer) {
        return;
      }


      activeViewer.dragging =
        false;


      image.classList.remove(
        'is-dragging'
      );

    };


    image.addEventListener(
      'pointerup',
      stopDragging
    );


    image.addEventListener(
      'pointercancel',
      stopDragging
    );


    document.documentElement
      .classList.add(
        'MooaImageViewerOpen'
      );


    renderViewerItem();


    requestAnimationFrame(
      () => {

        element.classList.add(
          'is-open'
        );


        element.setAttribute(
          'aria-hidden',
          'false'
        );


        element
          .querySelector(
            '.MooaImageViewer__Close'
          )
          ?.focus();

      }
    );

  }


  /* =========================================================
     EVENTOS DESKTOP
     ========================================================= */

  function handleThumbnailClick(
    event
  ) {

    if (
      !DESKTOP_MEDIA_QUERY.matches
    ) {
      return;
    }


    const thumbnail =
      event.target.closest(
        THUMBNAIL_SELECTOR
      );


    if (!thumbnail) {
      return;
    }


    const gallery =
      thumbnail.closest(
        GALLERY_SELECTOR
      );


    if (!gallery) {
      return;
    }


    event.preventDefault();

    event.stopPropagation();


    selectMedia(
      gallery,
      thumbnail.dataset.mediaId
    );

  }


  function handleImageClick(
    event
  ) {

    if (
      !DESKTOP_MEDIA_QUERY.matches
    ) {
      return;
    }


    const slide =
      event.target.closest(
        IMAGE_SLIDE_SELECTOR
      );


    if (!slide) {
      return;
    }


    const gallery =
      slide.closest(
        GALLERY_SELECTOR
      );


    if (!gallery) {
      return;
    }


    /*
     * Capturamos antes que Prestige
     * para evitar PhotoSwipe nativo.
     */

    event.preventDefault();

    event.stopPropagation();

    event.stopImmediatePropagation?.();


    openViewer(
      gallery,
      slide.dataset.mediaId,
      slide
    );

  }


  /* =========================================================
     TECLADO
     ========================================================= */

  function handleKeyboard(
    event
  ) {

    if (activeViewer) {

      if (
        event.key === 'Escape'
      ) {

        event.preventDefault();

        closeViewer();

      } else if (
        event.key === 'ArrowLeft'
      ) {

        event.preventDefault();

        changeViewerItem(-1);

      } else if (
        event.key === 'ArrowRight'
      ) {

        event.preventDefault();

        changeViewerItem(1);

      } else if (
        event.key === '+' ||
        event.key === '='
      ) {

        event.preventDefault();

        setViewerScale(
          activeViewer.scale +
          SCALE_STEP
        );

      } else if (
        event.key === '-'
      ) {

        event.preventDefault();

        setViewerScale(
          activeViewer.scale -
          SCALE_STEP
        );

      }

      return;

    }


    if (
      !DESKTOP_MEDIA_QUERY.matches ||
      ![
        'ArrowUp',
        'ArrowDown'
      ].includes(
        event.key
      )
    ) {
      return;
    }


    const gallery =
      event.target.closest(
        GALLERY_SELECTOR
      );


    if (!gallery) {
      return;
    }


    const thumbnails =
      getThumbnails(
        gallery
      );


    if (
      thumbnails.length < 2
    ) {
      return;
    }


    const currentIndex =
      thumbnails.findIndex(
        (thumbnail) =>
          thumbnail.classList
            .contains(
              'is-selected'
            )
      );


    if (
      currentIndex < 0
    ) {
      return;
    }


    event.preventDefault();


    const movement =
      event.key ===
      'ArrowDown'
        ? 1
        : -1;


    const nextIndex =
      (
        currentIndex +
        movement +
        thumbnails.length
      ) %
      thumbnails.length;


    const nextThumbnail =
      thumbnails[
        nextIndex
      ];


    selectMedia(
      gallery,
      nextThumbnail.dataset.mediaId,
      {
        focusSlide: false
      }
    );


    nextThumbnail.focus({
      preventScroll: true
    });

  }


  /* =========================================================
     CAMBIO DESKTOP / MÓVIL
     ========================================================= */

  function handleBreakpointChange() {

    if (
      !DESKTOP_MEDIA_QUERY.matches
    ) {

      closeViewer();


      /*
       * Al entrar en móvil volvemos
       * a permitir una nueva precarga.
       */

      document
        .querySelectorAll(
          GALLERY_SELECTOR
        )
        .forEach(
          (gallery) => {

            delete gallery.dataset
              .mooaMobilePreloaded;

            preloadMobileGallery(
              gallery
            );

          }
        );


      return;

    }


    document
      .querySelectorAll(
        GALLERY_SELECTOR
      )
      .forEach(
        (gallery) => {

          const selectedMediaId =
            getInitialMediaId(
              gallery
            );


          if (
            selectedMediaId
          ) {

            selectMedia(
              gallery,
              selectedMediaId,
              {
                scrollThumbnail:
                  false
              }
            );

          }

        }
      );

  }


  /* =========================================================
     START
     ========================================================= */

  function startMooaProductGallery() {

    initialiseAllGalleries();


    /*
     * Segunda pasada móvil después
     * de que lazysizes haya arrancado.
     */

    if (
      !DESKTOP_MEDIA_QUERY.matches
    ) {

      window.setTimeout(
        () => {

          document
            .querySelectorAll(
              GALLERY_SELECTOR
            )
            .forEach(
              (gallery) => {

                delete gallery
                  .dataset
                  .mooaMobilePreloaded;

                preloadMobileGallery(
                  gallery
                );

              }
            );

        },
        350
      );

    }


    document.addEventListener(
      'click',
      handleImageClick,
      true
    );


    document.addEventListener(
      'click',
      handleThumbnailClick,
      true
    );


    document.addEventListener(
      'keydown',
      handleKeyboard
    );


    DESKTOP_MEDIA_QUERY
      .addEventListener?.(
        'change',
        handleBreakpointChange
      );


    const observer =
      new MutationObserver(
        (mutations) => {

          mutations.forEach(
            (mutation) => {

              mutation.addedNodes
                .forEach(
                  (node) => {

                    if (
                      !(
                        node
                        instanceof
                        HTMLElement
                      )
                    ) {
                      return;
                    }


                    initialiseAllGalleries(
                      node
                    );


                    preloadAllMobileGalleries(
                      node
                    );

                  }
                );

            }
          );

        }
      );


    observer.observe(
      document.body,
      {
        childList: true,
        subtree: true
      }
    );

  }


  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      startMooaProductGallery,
      {
        once: true
      }
    );

  } else {

    startMooaProductGallery();

  }

})();